import React, { FC } from 'react'
import { useMutation } from '@apollo/client'
import { ApolloError } from '@apollo/client/errors'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { CREATE_PRODUCT } from 'queries/products'
import { createProduct, createProductVariables } from '__generatedTypes__/createProduct'
import { ProductStatus, ProductTypeEnum } from '__generatedTypes__/globalTypes'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { toast } from 'utils/toast'
import { convertToCents } from 'utils/priceFormatter'
import { useImageUpload } from 'utils/saveImageS3'
import { STRIPE_PRODUCT_IMAGES_PATH_S3 } from 'constants/images'
import { FIND_ALL_UPGRADE_PAGES } from 'queries/upgradePage'
import { getUpgradePageQueryVariables } from 'pagesNew/UpgradePage/ProductPricing/getUpgradePageQueryVariables'
import { Form, FormValues } from '../components/Form'

type Props = {
  platform: Platform
}

export const CreateExtension: FC = withPlatformCheck(({ platform }: Props) => {
  const { navigate } = useLink()
  const uploadImage = useImageUpload()
  const [createProductMutation] = useMutation<createProduct, createProductVariables>(CREATE_PRODUCT)

  const getInitialValues = (): FormValues | undefined => undefined

  const handleSubmit = async (values: FormValues) => {
    try {
      const imageFile = values.image?.[0]?.file
      const imageKey = imageFile && (await uploadImage(imageFile, STRIPE_PRODUCT_IMAGES_PATH_S3))

      await createProductMutation({
        variables: {
          createProductInput: {
            name: values.name,
            duration: String(values.duration),
            price: convertToCents(values.price),
            originalPrice: convertToCents(values.originalPrice ?? 0),
            description: values.description ?? '',
            imageKey: imageKey ?? '',
            type: ProductTypeEnum.Extension,
            status: ProductStatus.Active,
            isExtension: true,
            platform: { id: platform.id },
          },
        },
        refetchQueries: [
          {
            query: FIND_ALL_UPGRADE_PAGES,
            variables: getUpgradePageQueryVariables(platform.id, false),
          },
        ],
        awaitRefetchQueries: true,
      })

      navigate(cmsRoutes.PRODUCTS, undefined, {
        searchParams: new URLSearchParams({ tab: 'extensions' }),
      })

      toast.success('Extension created successfully')
    } catch (error) {
      if (error && typeof error === 'object' && 'graphQLErrors' in error) {
        return toast.error((error as ApolloError).graphQLErrors?.[0]?.message as string)
      }

      return toast.error('Failed to create extension')
    }
  }

  return <Form getInitialValues={getInitialValues} onSubmit={handleSubmit} isExtension mode="create" />
})
